import { Grid, Typography, FormControlLabel, Checkbox, Select, MenuItem, RadioGroup, Radio } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useState, useEffect, useCallback } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const exportProperties = [
  'createDate',
  'memberNr',
  'sex',
  'firstName',
  'lastName',
  'birthDate',
  'address',
  'postNr',
  'postAddress',
  'email',
  'phone',
  'phoneOther',
  'newspaper',
  'localAss',
  'district',
  'country',
  'customProfile1',
  'customProfile2',
  'customProfile3',
  'customProfile4',
  'customProfile5',
  'customProfile6',
  'customProfile7',
  'customProfile8',
  'customProfile9',
  'customProfile10',
  'custom1',
  'custom2',
  'custom3',
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
  'admDate',
  'expireDate',
  'resDate',
  'age',
] as const;

// Define a type for the form fields based on the exportProperties
type FormFields = {
  [key in (typeof exportProperties)[number]]: boolean;
} & {
  sortField: (typeof exportProperties)[number]; // Add sortOrder as a separate property
  orderDirection: 'asc' | 'desc'; // Add orderDirection as a separate property
  familyHeadMemberNr: boolean;
};

type MemberAdvancedSearchExportFilterProps = {
  updateState: (state: Map<string, string | number | boolean>) => void;
};

export const MemberAdvancedSearchExportFilter = (props: MemberAdvancedSearchExportFilterProps) => {
  const { updateState } = props;
  const { t } = useTranslation(['member']);

  const { handleSubmit, control, setValue, getValues } = useForm<FormFields>({
    defaultValues: {
      ...exportProperties.reduce((acc, prop) => {
        acc[prop] = true;
        return acc;
      }, {} as FormFields),
      sortField: 'memberNr', // Default sort field
      orderDirection: 'asc', // Default sort order
      familyHeadMemberNr: false,
    },
  });

  // Form submission handler
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = (data: FormFields) => {
    return true;
  };

  // State for master checkbox
  const [allChecked, setAllChecked] = useState(true);
  // Watch form values
  const values = useWatch({ control });

  // Method to serialize state
  const serializeState = useCallback(() => {
    // Serialize state into an array of entries, filtering out falsy values
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const serializedState: [string, string | number | boolean][] = Object.entries(getValues()).filter(([_, value]) => !!value) as [
      string,
      string | number | boolean,
    ][];
    updateState(new Map(serializedState));
  }, [getValues, updateState]);

  // Serialize the state when any form value is changed
  useEffect(() => {
    serializeState();
  }, [serializeState, values]);

  // Toggle all checkboxes when master checkbox changes
  const handleMasterCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setAllChecked(isChecked);
    exportProperties.forEach((prop) => setValue(prop, isChecked));
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ p: 3, mt: -2 }}>
      <Grid container>
        <Grid item xs={12} sx={{ mb: 1 }}>
          <Typography variant='h6'>{t('member:exportModal.selectColumnTitle')}</Typography>
          <Typography variant='body2'>{t('member:exportModal.selectColumnDescription')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            sx={{ '& > .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 600 } }}
            control={<Checkbox checked={allChecked} onChange={handleMasterCheckboxChange} />}
            label={t('member:exportModal.checkAll')}
          />
        </Grid>
        {exportProperties.map((prop) => (
          <Grid item xs={6} md={4} lg={3} key={prop}>
            <Controller
              key={prop}
              name={prop}
              control={control}
              render={({ field }) => (
                <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label={t(`member:exportModal.formField.${prop}`)} />
              )}
            />
          </Grid>
        ))}
        <Grid item xs={12} sx={{ mt: 2 }} />
        <Grid item xs={12} md={8} lg={9}>
          <Typography variant='h6' sx={{ mb: 1 }}>
            {t('member:exportModal.sortHeader')}
          </Typography>
          <Stack direction='row' spacing={2}>
            <Controller
              name='sortField'
              control={control}
              render={({ field }) => (
                <Select {...field} size='small' fullWidth>
                  {exportProperties.map((property) => (
                    <MenuItem key={property} value={property}>
                      {t(`member:exportModal.formField.${property}`)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name='orderDirection'
              control={control}
              render={({ field }) => (
                <Box sx={{ minWidth: '15rem' }}>
                  <RadioGroup row {...field} value={field.value}>
                    <FormControlLabel value='asc' control={<Radio />} label={t('member:exportModal.sort.asc')} />
                    <FormControlLabel value='desc' control={<Radio />} label={t('member:exportModal.sort.desc')} />
                  </RadioGroup>
                </Box>
              )}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Typography variant='h6' sx={{ mb: 1 }}>
            {t('member:exportModal.familyHeader')}
          </Typography>
          <Controller
            name='familyHeadMemberNr'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={t(`member:exportModal.formField.familyHeadMemberNr`)}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
